<template>
    <div>
        <bs5-page-header :title="$t('clients.title')" :subtitle="$t('clients.subtitle')">
            <template #actions>
                <bs5-base-button variant="secondary" :loading="loading" @click="fetchAll">
                    <i v-if="!loading" class="bi bi-arrow-clockwise"></i>{{ $t("common.refresh") }}
                </bs5-base-button>
                <bs5-base-button variant="primary" :disabled="distributors.length === 0" @click="openAddClientModal">
                    <i class="bi bi-plus-lg"></i>{{ $t("clients.new") }}
                </bs5-base-button>
            </template>
        </bs5-page-header>

        <div class="au-card">
            <div class="au-card-head">
                <h2 class="au-card-title"><i class="bi bi-list-ul"></i>{{ $t("clients.list") }}</h2>
                <div v-if="distributors.length > 1" class="au-select">
                    <i class="bi bi-shop"></i>
                    <select v-model="selectedDistributorId" class="form-select" :aria-label="$t('clients.distributor')">
                        <option value="">{{ $t("clients.allDistributors", { count: clients.length }) }}</option>
                        <option v-for="option in distributorOptions" :key="option.id" :value="option.id">
                            {{ $t("clients.distributorOption", { name: option.name, count: option.count }) }}
                        </option>
                    </select>
                </div>
                <div class="au-search ms-auto">
                    <i class="bi bi-search"></i>
                    <input v-model="search" type="search" class="form-control" :placeholder="$t('clients.search')"
                        :aria-label="$t('clients.search')" />
                </div>
            </div>

            <div v-if="loading" class="au-empty">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ $t("common.loading") }}</span>
                </div>
            </div>
            <div v-else-if="clients.length === 0" class="au-empty">
                <i class="bi bi-briefcase"></i>
                <div class="au-empty-title">{{ $t("clients.emptyTitle") }}</div>
                <div>{{ $t("clients.emptyText") }}</div>
            </div>
            <div v-else-if="filteredClients.length === 0" class="au-empty">
                <i class="bi bi-search"></i>
                <div>{{ $t("clients.noResults") }}</div>
            </div>
            <div v-else class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th v-for="column in columns" :key="column.key" :aria-sort="ariaSort(column.key)">
                                <button type="button" class="au-th-sort" :class="{ 'is-sorted': sortKey === column.key }"
                                    :title="$t('clients.sortBy', { column: $t(column.labelKey) })"
                                    @click="sortBy(column.key)">
                                    <i :class="['bi', column.icon]"></i>{{ $t(column.labelKey) }}
                                    <i :class="['bi', sortIcon(column.key)]"></i>
                                </button>
                            </th>
                            <th class="text-end">{{ $t("common.actions") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="client in pagedClients" :key="client.id as string" class="au-row-link" tabindex="0"
                            @click="openPanel(client)" @keydown.enter="openPanel(client)">
                            <td>
                                <div class="au-entity">
                                    <div class="au-avatar"><i class="bi bi-buildings"></i></div>
                                    <div style="min-width: 0;">
                                        <div class="au-entity-name">{{ client.name }}</div>
                                        <div class="au-entity-sub">
                                            <i class="bi bi-chat-left-text"></i>
                                            {{ client.description || $t("clients.noDescription") }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <span class="au-chip" :class="`is-${client.tone}`">
                                    <i class="bi bi-shop"></i>{{ client.distributorName }}
                                </span>
                            </td>
                            <td>
                                <template v-if="summaries[client.id as string]">
                                    <div class="au-lic-status" :class="`is-${summaries[client.id as string].tone}`">
                                        <span class="au-lic-dot"></span>{{ summaries[client.id as string].text }}
                                    </div>
                                    <div v-if="summaries[client.id as string].detail" class="au-lic-sub">
                                        {{ summaries[client.id as string].detail }}
                                    </div>
                                </template>
                                <div v-else class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">{{ $t("common.loading") }}</span>
                                </div>
                            </td>
                            <td @click.stop @keydown.enter.stop>
                                <div class="au-actions">
                                    <bs5-base-button variant="soft" size="sm"
                                        @click="openGenerateLicenseModal(client.id as string)">
                                        <i class="bi bi-file-earmark-plus"></i>{{ $t("clients.generateLicense") }}
                                    </bs5-base-button>
                                    <bs5-action-menu :label="$t('clients.more')">
                                        <button type="button" class="au-menu-item" role="menuitem"
                                            @click="openPanel(client)">
                                            <i class="bi bi-layout-sidebar-reverse"></i>{{ $t("clients.openPanel") }}
                                        </button>
                                        <button type="button" class="au-menu-item" role="menuitem"
                                            @click="editClient(client.id as string)">
                                            <i class="bi bi-pencil"></i>{{ $t("common.edit") }}
                                        </button>
                                        <div class="au-menu-sep"></div>
                                        <button type="button" class="au-menu-item is-danger" role="menuitem"
                                            @click="deleteClient(client)">
                                            <i class="bi bi-trash3"></i>{{ $t("clients.deleteClient") }}
                                        </button>
                                    </bs5-action-menu>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="!loading && filteredClients.length > 0" class="au-card-foot">
                <span>{{ $t("common.range", { from: rangeFrom, to: rangeTo, total: filteredClients.length }) }}</span>
                <span class="d-none d-md-inline"><i class="bi bi-hand-index me-1"></i>{{ $t("clients.rowHint") }}</span>
                <div v-if="pageCount > 1" class="au-pages">
                    <button type="button" :disabled="page === 1" :aria-label="$t('common.previous')" @click="page--">
                        <i class="bi bi-chevron-left"></i>
                    </button>
                    <button v-for="pageNumber in pageCount" :key="pageNumber" type="button"
                        :class="{ 'is-active': pageNumber === page }" @click="page = pageNumber">
                        {{ pageNumber }}
                    </button>
                    <button type="button" :disabled="page === pageCount" :aria-label="$t('common.next')"
                        @click="page++">
                        <i class="bi bi-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <ClientPanel :visible="panelOpen" :client="panelClient" :licenses="panelLicenses"
            :loading="panelClient ? !licensesByClient[panelClient.id as string] : false" :newLicenseIds="newLicenseIds"
            @close="closePanel" @edit="editClient" @issue="openGenerateLicenseModal"
            @changed="reloadLicenses(panelClientId)" />

        <AddClientModal :visible="showAddClientModal" :distributorId="addDistributorId" :distributors="distributors"
            @close="showAddClientModal = false" @client-added="fetchAll" />
        <EditClientModal :visible="showEditClientModal" :clientId="selectedClientId"
            @close="() => { showEditClientModal = false; selectedClientId = '' }" @client-updated="fetchAll" />
        <GenerateLicenseModal :visible="showGenerateLicenseModal" :clientId="selectedClientId"
            @close="() => { showGenerateLicenseModal = false; selectedClientId = '' }"
            @license-generated="onLicenseGenerated" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { t } from "@/helpers/i18n";
import i18n from "@/helpers/i18n";
import AddClientModal from "@/views/clients/modals/AddClientModal.vue";
import EditClientModal from "@/views/clients/modals/EditClientModal.vue";
import GenerateLicenseModal from "@/views/licenses/modals/GenerateLicenseModal.vue";
import ClientPanel from "@/views/clients/parts/ClientPanel.vue";
import distributorService from "@/services/distributor.service";
import clientService from "@/services/client.service";
import licenseService from "@/services/license.service";
import { Distributor } from "@/models/distributor.model";
import { Client } from "@/models/client.model";
import { License } from "@/models/license.model";
import { showToast } from "@/utils/toast";
import { LicenseSummary, summarizeLicenses } from "@/utils/license-status";

type ClientRow = Client & { distributorName: string; tone: string };
type SortKey = "name" | "distributor" | "licenses";

const pageSize = 10;
const tones = ["indigo", "cyan", "rose", "amber", "emerald"];
// Liczba równoległych zapytań o licencje przy wczytywaniu listy
const licenseRequestConcurrency = 4;

export default defineComponent({
    components: { AddClientModal, EditClientModal, GenerateLicenseModal, ClientPanel },
    data() {
        return {
            distributors: [] as Distributor[],
            clients: [] as ClientRow[],
            licensesByClient: {} as Record<string, License[]>,
            selectedDistributorId: "",
            search: "",
            sortKey: "name" as SortKey,
            sortDirection: 1,
            page: 1,
            loading: false,
            showAddClientModal: false,
            showEditClientModal: false,
            showGenerateLicenseModal: false,
            selectedClientId: "",
            panelOpen: false,
            panelClientId: "",
            newLicenseIds: [] as string[],
            licenseIdsBeforeIssue: [] as string[],
            columns: [
                { key: "name" as SortKey, labelKey: "clients.client", icon: "bi-briefcase" },
                { key: "distributor" as SortKey, labelKey: "clients.distributor", icon: "bi-shop" },
                { key: "licenses" as SortKey, labelKey: "clients.licenses", icon: "bi-patch-check" },
            ],
        };
    },
    computed: {
        distributorOptions(): { id: string; name: string; count: number }[] {
            return this.distributors.map(distributor => ({
                id: distributor.id as string,
                name: distributor.name,
                count: this.clients.filter(client => client.distributorId === distributor.id).length,
            }));
        },
        summaries(): Record<string, LicenseSummary> {
            const result: Record<string, LicenseSummary> = {};
            for (const [clientId, licenses] of Object.entries(this.licensesByClient)) {
                result[clientId] = summarizeLicenses(licenses);
            }
            return result;
        },
        filteredClients(): ClientRow[] {
            const phrase = this.search.trim().toLocaleLowerCase();
            return this.clients.filter(client =>
                (!this.selectedDistributorId || client.distributorId === this.selectedDistributorId)
                && (!phrase
                    || client.name.toLocaleLowerCase().includes(phrase)
                    || (client.description ?? "").toLocaleLowerCase().includes(phrase)));
        },
        sortedClients(): ClientRow[] {
            const culture = i18n.global.locale.value;
            const byName = (a: ClientRow, b: ClientRow) => a.name.localeCompare(b.name, culture);
            // Klienci bez wczytanego statusu trafiają na koniec przy sortowaniu po licencjach
            const rank = (client: ClientRow) => this.summaries[client.id as string]?.rank ?? 99;
            return [...this.filteredClients].sort((a, b) => {
                let result = 0;
                if (this.sortKey === "distributor") {
                    result = a.distributorName.localeCompare(b.distributorName, culture);
                } else if (this.sortKey === "licenses") {
                    result = rank(a) - rank(b);
                } else {
                    result = byName(a, b);
                }
                return (result * this.sortDirection) || byName(a, b);
            });
        },
        pageCount(): number {
            return Math.max(1, Math.ceil(this.sortedClients.length / pageSize));
        },
        pagedClients(): ClientRow[] {
            const start = (this.page - 1) * pageSize;
            return this.sortedClients.slice(start, start + pageSize);
        },
        rangeFrom(): number {
            return (this.page - 1) * pageSize + 1;
        },
        rangeTo(): number {
            return Math.min(this.page * pageSize, this.sortedClients.length);
        },
        addDistributorId(): string {
            if (this.selectedDistributorId) {
                return this.selectedDistributorId;
            }
            return this.distributors.length === 1 ? (this.distributors[0].id as string) : "";
        },
        panelClient(): ClientRow | null {
            return this.clients.find(client => client.id === this.panelClientId) ?? null;
        },
        panelLicenses(): License[] {
            const licenses = this.licensesByClient[this.panelClientId] ?? [];
            // Data wystawienia pochodzi z pliku żądania i bywa identyczna, więc nowe licencje wymuszamy na górę
            const isNew = (license: License) => this.newLicenseIds.includes(license.id as string);
            return [...licenses].sort((a, b) =>
                (Number(isNew(b)) - Number(isNew(a))) || b.issuedDate.localeCompare(a.issuedDate));
        },
    },
    watch: {
        search() {
            this.page = 1;
        },
        selectedDistributorId() {
            this.page = 1;
        },
        pageCount(count: number) {
            if (this.page > count) {
                this.page = count;
            }
        },
    },
    methods: {
        async fetchAll() {
            this.loading = true;
            try {
                const distributors = await distributorService.getDistributors();
                const clientLists = await Promise.all(
                    distributors.map(distributor => clientService.getClientsByDistributor(distributor.id as string)));
                this.distributors = distributors;
                this.clients = clientLists.flatMap((list, index) => list.map(client => ({
                    ...client,
                    distributorName: distributors[index].name,
                    tone: tones[index % tones.length],
                })));
                this.licensesByClient = {};
            } catch (error) {
                showToast(t("common.loadFailed"), "error");
                console.error("Error fetching clients:", error);
            } finally {
                this.loading = false;
            }
            this.loadAllLicenses();
        },
        async loadAllLicenses() {
            // Statusy wczytujemy dla wszystkich klientów, żeby sortowanie po licencjach obejmowało całą listę
            const queue = this.clients.map(client => client.id as string);
            const worker = async () => {
                while (queue.length > 0) {
                    await this.reloadLicenses(queue.shift() as string);
                }
            };
            await Promise.all(Array.from({ length: licenseRequestConcurrency }, worker));
        },
        async reloadLicenses(clientId: string) {
            if (!clientId) {
                return;
            }
            try {
                this.licensesByClient[clientId] = await licenseService.getClientLicenses(clientId);
            } catch (error) {
                console.error("Error fetching licenses:", error);
            }
        },
        sortBy(key: SortKey) {
            this.sortDirection = this.sortKey === key ? -this.sortDirection : 1;
            this.sortKey = key;
            this.page = 1;
        },
        sortIcon(key: SortKey): string {
            if (this.sortKey !== key) {
                return "bi-arrow-down-up";
            }
            return this.sortDirection === 1 ? "bi-sort-down-alt" : "bi-sort-up";
        },
        ariaSort(key: SortKey): string {
            if (this.sortKey !== key) {
                return "none";
            }
            return this.sortDirection === 1 ? "ascending" : "descending";
        },
        openPanel(client: ClientRow) {
            this.panelClientId = client.id as string;
            this.newLicenseIds = [];
            this.panelOpen = true;
            if (!this.licensesByClient[this.panelClientId]) {
                this.reloadLicenses(this.panelClientId);
            }
        },
        closePanel() {
            this.panelOpen = false;
            this.newLicenseIds = [];
        },
        openAddClientModal() {
            this.showAddClientModal = true;
        },
        editClient(clientId: string) {
            this.selectedClientId = clientId;
            this.showEditClientModal = true;
        },
        async deleteClient(client: ClientRow) {
            const response = await this.$swal.fire({
                title: t("clients.deleteTitle", { name: client.name }),
                text: t("clients.deleteText"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t("common.confirmDeleteButton"),
                cancelButtonText: t("common.cancel"),
            });
            if (!response.isConfirmed) {
                return;
            }
            try {
                await clientService.deleteClient(client.id as string);
                showToast(t("clients.deleted"), "success");
                if (this.panelClientId === client.id) {
                    this.closePanel();
                }
                await this.fetchAll();
            } catch (error) {
                showToast(t("clients.deleteFailed"), "error");
                console.error("Error deleting client:", error);
            }
        },
        openGenerateLicenseModal(clientId: string) {
            this.selectedClientId = clientId;
            this.licenseIdsBeforeIssue = (this.licensesByClient[clientId] ?? []).map(license => license.id as string);
            this.showGenerateLicenseModal = true;
        },
        async onLicenseGenerated() {
            // Po wystawieniu pokazujemy panel klienta z nową licencją gotową do pobrania
            const clientId = this.selectedClientId;
            await this.reloadLicenses(clientId);
            this.panelClientId = clientId;
            this.newLicenseIds = (this.licensesByClient[clientId] ?? [])
                .map(license => license.id as string)
                .filter(id => !this.licenseIdsBeforeIssue.includes(id));
            this.panelOpen = true;
        },
    },
    mounted() {
        this.fetchAll();
    },
});
</script>
